/**
 * Sjekklister — checklists run for an event or a date.  MKK adoption Batch 1 (MKK_ADOPTION_BATCH_1.md §7); hardened
 * 2026-10-02.
 *
 * Behind the `checklists` capability. Tabs, in the order a volunteer needs them: «Mine» (runs with my items), «Aktive»
 * (every open run I may see: mine, and those with unassigned items anyone may take), «Ferdige» (finished runs I took part
 * in — where a mistaken last tick is undone), and — for checklists.manage — «Maler», the reusable lists. A run is a column
 * of big tick rows (at least 44 px), the label naming the checkbox, the note under it. A tick shows at once, says
 * «Lagrer …» until the server has it, and goes back (with the reason) if the server refuses; progress is announced as it
 * changes. A run COPIES its list when it starts, so editing a template never changes a run that is under way.
 *
 * Handing items out is a mode, not a side effect of a dropdown: «Fordel punkter» opens a picker per item, nothing is
 * saved until «Lagre fordelingen» — once, with the run's version — so moving through a list with the arrow keys never
 * saves anything (it used to save, and audit, on every arrow). A conflict keeps the choices made and asks.
 *
 * No public access: nobody without a staff account can open a checklist, and whoever ticks is that account.
 */
import React, { useEffect, useRef, useState } from 'react';
import { can, post, patch, send, errText, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useDirty } from '../lib/nav';
import { shortDate } from '../lib/format';
import { Btn, Card, Field, Loading, LoadError, Msg, Tag, TwoStep } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { BackButton, Conflict, EventSelect, MoreButton, PersonSelect, Progress, eventText, hashQuery, useAssignable, useEscapeToClose, useEvents,
  usePaged } from './work-ui';

type Tab = 'mine' | 'active' | 'done' | 'templates';
const PATHS: Record<Exclude<Tab, 'templates'>, string> = {
  mine: '/work/checklists/runs?mine=1', active: '/work/checklists/runs?status=open', done: '/work/checklists/runs?status=done',
};

export function Sjekklister() {
  const manage = can('checklists.manage');
  const start = hashQuery();
  const t0 = start.get('tab');
  const [tab, setTab] = useState<Tab>(t0 === 'active' || t0 === 'done' ? t0 : !can('work.assigned') ? 'active' : 'mine');
  const [open, setOpen] = useState<string | null>(start.get('new') ? 'new' : start.get('id'));   // a run id, 'new'
  // From a day in Årshjul: «Lag sjekkliste for denne dagen» opens the start form with that day and that planner item.
  const prefill = { dueOn: start.get('date') || '', plannerItemId: start.get('planner') || '' };
  const lastRow = useRef<string | null>(null);
  // Owner 2026-10-23: «Start sjekkliste» (and the ?new= link from Årshjul) opens the form in a popup; a run that exists
  // keeps the detail beside the list.
  const [newDirty, setNewDirty] = useState(false);
  const closeNew = () => { setOpen(null); setNewDirty(false); };
  const runs = usePaged(tab === 'templates' ? null : PATHS[tab]);
  const people = useAssignable(manage);
  const close = React.useCallback(() => {
    setOpen(null);
    setTimeout(() => (document.getElementById(lastRow.current ? 'run-row-' + lastRow.current : 'lists-head') as HTMLElement | null)?.focus(), 0);
  }, []);
  const items = runs.items;
  const empty = { mine: 'Du har ingen punkter å krysse av.', active: 'Ingen aktive sjekklister.', done: 'Ingen ferdige sjekklister du var med på.' };
  return (
    <div className={'work-split' + (open && open !== 'new' ? ' has-detail' : '')}>
      <Card className="work-list" title={<span id="lists-head" tabIndex={-1}>Sjekklister</span>}
            hint="Faste lister som gjennomføres til et arrangement eller en dato — for eksempel fredagsforberedelser. Bare ansatte ser dem.">
        <div className="work-bar">
          <div className="bnr-seg work-seg" role="group" aria-label="Vis">
            <button type="button" aria-pressed={tab === 'mine'} onClick={() => setTab('mine')}>Mine</button>
            <button type="button" aria-pressed={tab === 'active'} onClick={() => setTab('active')}>Aktive</button>
            <button type="button" aria-pressed={tab === 'done'} onClick={() => setTab('done')}>Ferdige</button>
            {manage && <button type="button" aria-pressed={tab === 'templates'} onClick={() => { setTab('templates'); setOpen(null); }}>Maler</button>}
          </div>
          <span className="sp" />
          {manage && tab !== 'templates' && <Btn onClick={() => { lastRow.current = null; setOpen('new'); }}>Start sjekkliste</Btn>}
        </div>
        {tab === 'templates' ? <Templates /> : runs.error ? <LoadError error={runs.error} retry={runs.reload} /> : !runs.data ? <Loading /> : (
          <>
            <p className="vh" role="status">{runs.total === 1 ? '1 sjekkliste' : `${runs.total} sjekklister`}{items.length < runs.total ? `, ${items.length} vist` : ''}</p>
            {items.length === 0 && <p className="card-body work-empty">{empty[tab]}</p>}
            <ul className="work-rows">{items.map((r) => (
              <li key={r.id}>
                <button type="button" id={'run-row-' + r.id} className={'work-row' + (open === r.id ? ' sel' : '')} aria-current={open === r.id ? 'true' : undefined}
                        onClick={() => { lastRow.current = r.id; setOpen(r.id); }}>
                  <span className="work-row-title">{r.title}</span>
                  <span className="work-row-meta">
                    <Progress done={r.progress.done} total={r.progress.total} label={r.title} mine={r.progress.scope === 'mine'} />
                    {r.dueOn && <time dateTime={r.dueOn}>{shortDate(r.dueOn)}</time>}
                    {r.event && <span>· {eventText(r.event)}</span>}
                    {r.mine.open > 0 && <Tag tone="gold">{r.mine.open} til deg</Tag>}
                    {r.unassignedOpen > 0 && <span>{r.unassignedOpen} ledige</span>}
                  </span>
                </button>
              </li>
            ))}</ul>
            <MoreButton list={runs} />
          </>
        )}
      </Card>
      <FormDialog open={open === 'new' && tab !== 'templates'} title="Start sjekkliste" dirty={newDirty} onClose={closeNew}>
        {open === 'new' && tab !== 'templates' && <StartRun people={people} prefill={prefill} onDirty={setNewDirty} onCancel={closeNew}
          onStarted={(id) => { void runs.reload(); setNewDirty(false); lastRow.current = id; setOpen(id); }} />}
      </FormDialog>
      {open && open !== 'new' && tab !== 'templates' && <RunDetail key={open} id={open} people={people} onClose={close} onChanged={() => void runs.reload()} />}
    </div>
  );
}

/** The form to start a run, in the popup (its title, «×» and Escape are FormDialog's). */
function StartRun({ people, prefill, onCancel, onStarted, onDirty }: {
  people: { id: string; name: string }[]; prefill: { dueOn: string; plannerItemId: string }; onCancel: () => void; onStarted: (id: string) => void;
  onDirty: (dirty: boolean) => void;
}) {
  const tpl = useLoad<Body>('/work/checklists/templates');
  const ev = useEvents();
  const [f, setF] = useState({ templateId: '', dueOn: prefill.dueOn, eventId: '', assigneeId: '', title: '' });
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  const dirty = Boolean(f.templateId || f.title) || busy;
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);
  const active: Body[] = ((tpl.data && tpl.data.items) || []).filter((t: Body) => t.state === 'active');
  const go = async () => {
    setBusy(true); say('Starter …');
    const r = await post('/work/checklists/runs', { templateId: f.templateId, dueOn: f.dueOn || null, assigneeId: f.assigneeId || null, title: f.title || null,
      plannerItemId: prefill.plannerItemId || null, ...(ev.allowed && f.eventId ? { eventId: f.eventId } : {}) });
    setBusy(false);
    if (!r.ok) return say(errText(r.body, { templateId: 'Sjekkliste', dueOn: 'Dato', eventId: 'Arrangement', assigneeId: 'Ansvarlig' }), 'err');
    onStarted(r.body.run.id);
  };
  return (
    <div>
        {tpl.error ? <LoadError error={tpl.error} retry={tpl.reload} /> : !tpl.data ? <Loading /> : active.length === 0 ? (
          <p>Lag en mal under «Maler» først.</p>
        ) : (
          <>
            <Field label="Sjekkliste" required>{(id) => (
              <select id={id} value={f.templateId} onChange={(e) => setF({ ...f, templateId: e.target.value })}>
                <option value="">Velg …</option>{active.map((t) => <option key={t.id} value={t.id}>{t.title} ({t.items.length} punkter)</option>)}
              </select>
            )}</Field>
            <Field label="Navn" note="Valgfritt. Uten navn brukes malens.">{(id, nid) => <input id={id} value={f.title} maxLength={120} aria-describedby={nid} onChange={(e) => setF({ ...f, title: e.target.value })} />}</Field>
            <Field label="Dato">{(id) => <input id={id} type="date" value={f.dueOn} onChange={(e) => setF({ ...f, dueOn: e.target.value })} />}</Field>
            {prefill.plannerItemId && <p className="note">Kobles til punktet i årshjulet du kom fra.</p>}
            {ev.allowed && <Field label="Arrangement" note="Sjekklisten beholder datoen sin om arrangementet flyttes; du får beskjed her.">{(id, nid) => (
              <EventSelect id={id} value={f.eventId} onChange={(v) => setF({ ...f, eventId: v })} events={ev.items} describedBy={nid} />
            )}</Field>}
            <Field label="Ansvarlig for alle punktene" note="Valgfritt. Uten ansvarlig kan alle med tilgang krysse av; du kan fordele punktene etterpå.">{(id, nid) => (
              <PersonSelect id={id} value={f.assigneeId} onChange={(v) => setF({ ...f, assigneeId: v })} people={people} none="Ingen (ledige punkter)" describedBy={nid} />
            )}</Field>
            <div className="row"><Btn small={false} disabled={!f.templateId || busy} aria-busy={busy || undefined} onClick={go}>{busy ? 'Starter …' : 'Start'}</Btn><Btn ghost onClick={onCancel}>Avbryt</Btn></div>
          </>
        )}
        <Msg text={msg.text} tone={msg.tone} />
    </div>
  );
}

function RunDetail({ id, people, onClose, onChanged }: { id: string; people: { id: string; name: string }[]; onClose: () => void; onChanged: () => void }) {
  const { data, error, reload, setData } = useLoad<Body>('/work/checklists/runs/' + id);
  const [msg, say] = useMsg();
  const [said, setSaid] = useState('');
  // A tick shows at once (pending), and goes back if the server refuses it.
  const [pending, setPending] = useState<Record<string, boolean>>({});
  // «Fordel punkter»: item id → staff id ('' = ledig), held here until saved once.
  const [assigning, setAssigning] = useState<Record<string, string> | null>(null);
  const [conflict, setConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  const head = useRef<HTMLSpanElement>(null);
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (data && !focused) { head.current?.focus(); setFocused(true); } }, [data, focused]);
  const changedAssign = data && assigning ? (data.items as Body[]).filter((it) => (it.assignee ? it.assignee.id : '') !== assigning[it.id]) : [];
  useDirty('checklist-assign', Boolean(changedAssign.length));
  useEscapeToClose(head, onClose, Boolean(changedAssign.length) || busy || Object.keys(pending).length > 0);
  if (error) return <Card className="work-detail"><div className="card-body"><BackButton onClick={onClose} what="sjekklisten" /></div><LoadError error={error} retry={reload} /></Card>;
  if (!data) return <Card className="work-detail"><Loading /></Card>;
  const run: Body = data.run;
  const manage = data.can && data.can.manage;
  const tick = async (item: Body, done: boolean) => {
    setPending((p) => ({ ...p, [item.id]: done }));
    setSaid(`${item.label}: lagrer …`);
    const r = await send(`/work/checklists/runs/${id}/items/${item.id}`, { method: 'PUT', body: JSON.stringify({ done }) });
    setPending((p) => { const n = { ...p }; delete n[item.id]; return n; });
    if (!r.ok) { setSaid(`${item.label}: ikke lagret.`); return say(errText(r.body), 'err'); }
    setData(r.body); onChanged();
    const p = r.body.run.progress;
    setSaid(`${item.label}: ${done ? 'krysset av' : 'ikke ferdig'}. ${p.done} av ${p.total}${p.scope === 'mine' ? ' av dine' : ''} ferdig.${r.body.run.status === 'done' ? (p.scope === 'mine' ? ' Alle dine punkter er ferdige.' : ' Sjekklisten er ferdig.') : ''}`);
  };
  const change = async (body: Body, done: string, fromAssign = false) => {
    setBusy(true); say('Lagrer …');
    const r = await patch('/work/checklists/runs/' + id, { ...body, version: run.version });
    setBusy(false);
    if (r.status === 409 && r.body.run) {
      setData(r.body);
      if (fromAssign) { setConflict(true); return say('', ''); }
      return say(r.body.message + ' Endringen din ble ikke lagret.', 'warn');
    }
    if (!r.ok) return say(errText(r.body), 'err');
    setData(r.body); setConflict(false); if (fromAssign) setAssigning(null); onChanged(); say(done, 'ok');
  };
  const saveAssign = () => change({ items: changedAssign.map((it) => ({ id: it.id, assigneeId: assigning![it.id] || null })) },
    changedAssign.length === 1 ? 'Ett punkt har fått ny ansvarlig.' : `${changedAssign.length} punkter har fått ny ansvarlig.`, true);
  return (
    <Card className="work-detail" title={<span ref={head} tabIndex={-1}>{run.title}</span>}>
      <div className="card-body">
        <BackButton onClick={onClose} what="sjekklisten" />
        <p className="work-row-meta">
          {run.status === 'done' ? <Tag tone="green">Ferdig</Tag> : run.status === 'cancelled' ? <Tag tone="grey">Avbrutt</Tag> : <Tag tone="gold">Pågår</Tag>}
          <Progress done={run.progress.done} total={run.progress.total} label={run.title} mine={run.progress.scope === 'mine'} />
          {run.dueOn && <time dateTime={run.dueOn}>{shortDate(run.dueOn)}</time>}
          {run.event && <span>· {eventText(run.event)}</span>}
        </p>
        {/* The run kept its date when the event moved (it does not follow): said to whoever may see the event. */}
        {run.eventDateDiffers && run.event && run.event.startsOn && (
          <div className="msg warn" role="note">
            <p>Arrangementet er nå <time dateTime={run.event.startsOn}>{shortDate(run.event.startsOn)}</time>, men sjekklisten har fortsatt datoen <time dateTime={run.dueOn}>{shortDate(run.dueOn)}</time>.</p>
            {manage && run.status !== 'cancelled' && <Btn disabled={busy} onClick={() => change({ dueOn: run.event.startsOn }, 'Sjekklisten har fått arrangementets dato.')}>Flytt sjekklisten til {shortDate(run.event.startsOn)}</Btn>}
          </div>
        )}
        <p className="vh" role="status" aria-live="polite">{said}</p>
        {conflict && assigning && <Conflict what="sjekklisten" busy={busy} onKeepMine={saveAssign}
          onTakeTheirs={() => { setConflict(false); setAssigning(null); say('Fordelingen din er forkastet. Dette er den nyeste versjonen.', 'ok'); }} />}
        <ul className="work-ticks">{(data.items as Body[]).map((it) => {
          const shown = it.id in pending ? pending[it.id] : it.done;
          return (
            <li key={it.id} className={(shown ? 'done' : '') + (it.id in pending ? ' pending' : '')}>
              <label className="work-tick">
                <input type="checkbox" checked={shown} disabled={!it.canTick || it.id in pending} aria-busy={it.id in pending || undefined}
                       onChange={(e) => tick(it, e.target.checked)} aria-describedby={'state-' + it.id + (it.note ? ' note-' + it.id : '')} />
                <span><b>{it.label}</b>{it.mine && <> <Tag tone="gold">Ditt</Tag></>}
                  {it.note && <span className="note" id={'note-' + it.id}>{it.note}</span>}
                  <span className="note" id={'state-' + it.id}>{it.id in pending ? 'Lagrer …'
                    : it.done ? `Krysset av${it.doneBy ? ' — ' + it.doneBy.name : ''}` : it.assignee ? `Ansvarlig: ${it.assignee.name}` : 'Ledig — hvem som helst kan ta det'}</span>
                </span>
              </label>
              {assigning && (
                <Field label={`Ansvarlig for «${it.label}»`} className="field work-assign">{(fid) => (
                  <PersonSelect id={fid} value={assigning[it.id] || ''} people={people} none="Ledig"
                    onChange={(v) => setAssigning({ ...assigning, [it.id]: v })} />
                )}</Field>
              )}
            </li>
          );
        })}</ul>
        {manage && run.status !== 'cancelled' && (
          <div className="row" role="group" aria-label="Fordel punkter">
            {!assigning
              ? <Btn ghost onClick={() => setAssigning(Object.fromEntries((data.items as Body[]).map((it) => [it.id, it.assignee ? it.assignee.id : ''])))}>Fordel punkter</Btn>
              : <>
                  {!conflict && <Btn disabled={!changedAssign.length || busy} aria-busy={busy || undefined} onClick={saveAssign}>
                    {busy ? 'Lagrer …' : changedAssign.length ? `Lagre fordelingen (${changedAssign.length})` : 'Lagre fordelingen'}</Btn>}
                  <Btn ghost disabled={busy} onClick={() => { setAssigning(null); setConflict(false); }}>Avbryt fordelingen</Btn>
                </>}
          </div>
        )}
        {manage && (
          <div className="row">
            {run.status === 'cancelled'
              ? <Btn disabled={busy} onClick={() => change({ status: 'open' }, 'Sjekklisten er åpnet igjen.')}>Åpne igjen</Btn>
              : <TwoStep label="Avbryt sjekklisten" confirmLabel="Ja, avbryt den" disabled={busy} onConfirm={() => change({ status: 'cancelled' }, 'Sjekklisten er avbrutt.')} />}
          </div>
        )}
        <Msg text={msg.text} tone={msg.tone} />
      </div>
    </Card>
  );
}

/* ---- templates (checklists.manage) ---- */
function Templates() {
  const { data, error, reload } = useLoad<Body>('/work/checklists/templates');
  const [editing, setEditing] = useState<Body | null>(null);
  // Owner 2026-10-23: «+ Ny mal» and a template opened to change it are in a popup over the list.
  const [editDirty, setEditDirty] = useState(false);
  const closeEdit = () => { setEditing(null); setEditDirty(false); };
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const items: Body[] = data.items || [];
  return (
    <div className="card-body">
      <Btn onClick={() => setEditing({ title: '', description: '', state: 'active', items: [{ label: '', note: '' }] })}>+ Ny mal</Btn>
      {items.length === 0 && <p className="work-empty">Ingen maler ennå. En mal er en fast liste, for eksempel «Fredagsforberedelser».</p>}
      <ul className="work-rows">{items.map((t) => (
        <li key={t.id}><button type="button" className="work-row" onClick={() => setEditing(t)}>
          <span className="work-row-title">{t.title}</span>
          <span className="work-row-meta">{t.items.length} punkter {t.state === 'archived' && <Tag tone="grey">Arkivert</Tag>}</span>
        </button></li>
      ))}</ul>
      <FormDialog open={Boolean(editing)} title={editing && editing.id ? 'Endre mal' : 'Ny mal'} dirty={editDirty} onClose={closeEdit}>
        {editing && <TemplateEditor template={editing} onDirty={setEditDirty} onDone={() => { closeEdit(); void reload(); }} />}
      </FormDialog>
    </div>
  );
}

function TemplateEditor({ template, onDone, onDirty }: { template: Body; onDone: () => void; onDirty: (dirty: boolean) => void }) {
  const init = () => ({ title: template.title || '', description: template.description || '', state: template.state || 'active',
    items: (template.items || []).map((i: Body) => ({ label: i.label || '', note: i.note || '' })) as { label: string; note: string }[] });
  const [base] = useState(init);
  const [f, setF] = useState(init);
  const [msg, say] = useMsg();
  const [said, setSaid] = useState('');
  const [saving, setSaving] = useState(false);
  useDirty('checklist-template', true);
  const dirty = JSON.stringify(f) !== JSON.stringify(base);
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);
  const setItem = (i: number, k: 'label' | 'note', v: string) => setF({ ...f, items: f.items.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });
  const move = (i: number, by: number) => {
    const j = i + by;
    if (j < 0 || j >= f.items.length) return;
    const items = [...f.items];
    [items[i], items[j]] = [items[j], items[i]];
    setF({ ...f, items });
    setSaid(`«${items[j].label || 'Punktet'}» er flyttet til plass ${j + 1}.`);
  };
  const save = async () => {
    // QA C (2026-10-16): a double click on «Lagre malen» made two templates.
    if (saving) return;
    setSaving(true);
    const body = { title: f.title, description: f.description || null, state: f.state, items: f.items.map((i) => ({ label: i.label, note: i.note || null })) };
    const r = await (template.id ? send('/work/checklists/templates/' + template.id, { method: 'PUT', body: JSON.stringify(body) }) : post('/work/checklists/templates', body))
      .finally(() => setSaving(false));
    if (!r.ok) return say(errText(r.body, { title: 'Navn', items: 'Punkter' }), 'err');
    onDone();
  };
  return (
    <div>
      <p className="note">Endringer gjelder sjekklister som startes etterpå. De som allerede er i gang beholder sine punkter.</p>
      <Field label="Navn" required>{(id) => <input id={id} value={f.title} maxLength={120} onChange={(e) => setF({ ...f, title: e.target.value })} />}</Field>
      <Field label="Beskrivelse">{(id) => <textarea id={id} rows={2} value={f.description} maxLength={1000} onChange={(e) => setF({ ...f, description: e.target.value })} />}</Field>
      <p className="vh" role="status" aria-live="polite">{said}</p>
      <ol className="work-template-items">{f.items.map((it, i) => (
        <li key={i}>
          <Field label={`Punkt ${i + 1}`} required>{(id) => <input id={id} value={it.label} maxLength={200} onChange={(e) => setItem(i, 'label', e.target.value)} />}</Field>
          <Field label={`Merknad til punkt ${i + 1}`}>{(id) => <input id={id} value={it.note} maxLength={500} onChange={(e) => setItem(i, 'note', e.target.value)} />}</Field>
          <div className="row">
            <Btn ghost disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Flytt punkt ${i + 1} opp`}>Opp</Btn>
            <Btn ghost disabled={i === f.items.length - 1} onClick={() => move(i, 1)} aria-label={`Flytt punkt ${i + 1} ned`}>Ned</Btn>
            <Btn ghost disabled={f.items.length === 1} onClick={() => { setF({ ...f, items: f.items.filter((_, j) => j !== i) }); setSaid(`Punkt ${i + 1} er fjernet.`); }}
                 aria-label={`Fjern punkt ${i + 1}`}>Fjern</Btn>
          </div>
        </li>
      ))}</ol>
      <Btn ghost disabled={f.items.length >= 50} onClick={() => setF({ ...f, items: [...f.items, { label: '', note: '' }] })}>+ Legg til punkt</Btn>
      {template.id && (
        <Field label="Tilstand">{(id) => (
          <select id={id} value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })}><option value="active">Aktiv</option><option value="archived">Arkivert (kan ikke startes)</option></select>
        )}</Field>
      )}
      <div className="row"><Btn small={false} disabled={saving} aria-busy={saving || undefined} onClick={save}>{saving ? 'Lagrer …' : 'Lagre malen'}</Btn><Btn ghost disabled={saving} onClick={onDone}>Avbryt</Btn></div>
      <Msg text={msg.text} tone={msg.tone} />
    </div>
  );
}
